import React, { FC, useContext } from 'react';
import {
  DynamicHotelProps,
  GoToRoomDetailsParams,
  Hotel,
  HotelRoom,
  RoomClass,
} from 'ts/hotel/interfaces/HotelResponse';
import { getCurrency } from 'utils/utils';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import CancellationPolicyModal from '../Modals/CancellationPolicyModal';
import { FilterRoom } from 'ts/hotel/classes/FilterRoom';
import { useHotelProposal } from 'hooks/useProposal';
import RoomIcons from './RoomIcons';
import { formatPriceDynamic, formatPriceHotel } from 'utils/hotel';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import { MainContext } from 'contexts/MainContext';

interface HotelCardRoomItemProps {
  room: RoomClass;
  hotel: Hotel;
  hasImageSupplement?: boolean;
  highlightedUk?: string;
  grouped?: boolean;
  groupToggle?: React.ReactNode;
  extraClassName?: string;
  cancellationModalKey?: string;
  showRemarkModal?: boolean;
  dynamic?: DynamicHotelProps;
  filterRoom: FilterRoom;
  goToDetails: (params: GoToRoomDetailsParams) => void;
  cancellationModalVisibleIndex: string;
  setCancellationModalVisibleIndex: (index: string) => void;
  remarkModalVisibleIndex: string;
  setRemarkModalVisibleIndex: (index: string) => void;
}

const HotelCardRoomItem: FC<HotelCardRoomItemProps> = ({
  room,
  hotel,
  hasImageSupplement,
  highlightedUk,
  grouped = false,
  groupToggle,
  extraClassName = '',
  cancellationModalKey,
  showRemarkModal = !grouped,
  dynamic,
  filterRoom,
  goToDetails,
  cancellationModalVisibleIndex,
  setCancellationModalVisibleIndex,
  remarkModalVisibleIndex,
  setRemarkModalVisibleIndex,
}) => {
  const { filter } = useContext(HotelFilterContext);
  const { currency, priceMode } = useContext(MapHotelContext);
  const { user, userSiteConfig } = useContext(MainContext);
  const { toggleRoomProposal, isRoomInProposal } = useHotelProposal();

  const areRoomsColoured = dynamic ? false : userSiteConfig.hotel.areRoomsColoured;
  const isRoomNameCleared = dynamic ? false : userSiteConfig.hotel.isRoomNameCleared;
  const highlighted = highlightedUk === room.rcUniqueKey ? ' hotel-card-room-highlighted' : '';

  const getSuppText = () => {
    if (!user?.branch?.hotelDisplaySupplierName) return '';

    if (room.suppDesc) {
      return room.suppDesc;
    }

    const { item } = hotel;
    return (item.sysSupp ? item.sysSupp : '') + ' ' + (item.sysSuppCode ? item.sysSuppCode : '');
  };

  const getRoomsTypes = () => {
    if (user?.branchId === 15000) {
      return room.groupName;
    }
  };

  const getCxlClass = () => {
    if (
      new Date(room.cxl.limitDate) < new Date() &&
      new Date(room.cxl.limitDate) > new Date(2000, 1, 1)
    ) {
      return 'color-danger';
    }
  };

  const checkMatch = (match: string) => {
    if (
      '<span style="color:maroon">'.includes(match) ||
      '<span style="color:darkgreen">'.includes(match) ||
      '<span style="color:green">'.includes(match) ||
      '<span style="color:blue">'.includes(match) ||
      '<span style="color:purple">'.includes(match) ||
      '<span style="color:maroon">'.includes(match) ||
      '<span style="color:gold">'.includes(match) ||
      '<span style="color:brown">'.includes(match) ||
      '<span style="color:darkblue">'.includes(match) ||
      '<span class="highlight">'.includes(match) ||
      '</span>'.includes(match)
    ) {
      return match;
    }
  };

  const setRoomColored = (str: string, hotelRoom: HotelRoom) => {
    if (areRoomsColoured) {
      let result = str;
      const wordsColors = [
        { word: hotelRoom.grp, color: 'maroon' },
        { word: hotelRoom.cat, color: 'darkgreen' },
        { word: hotelRoom.typ, color: 'green' },
        { word: hotelRoom.bdr, color: 'blue' },
        { word: hotelRoom.vew, color: 'darkgreen' },
        { word: hotelRoom.bed, color: 'purple' },
        { word: hotelRoom.ada, color: 'gold' },
        { word: hotelRoom.pax, color: 'darkblue' },
        { word: hotelRoom.brd, color: 'brown' },
      ];

      wordsColors.forEach((wordColor) => {
        if (wordColor.word) {
          wordColor.word
            .replace(',')
            .split(' ')
            .forEach((word: string) => {
              result = result.replace(new RegExp(word, 'gi'), (match) => {
                if (checkMatch(match)) return match;
                return `<span style="color:${wordColor.color}">${match}</span>`;
              });
            });
        }
      });

      return result;
    }

    return str;
  };

  const getRoomName = (hotelRoom: HotelRoom) => {
    return isRoomNameCleared ? hotelRoom.displayNameCleared || '' : hotelRoom.displayName;
  };

  const highlightStr = (str: string, highlight: string, hotelRoom: HotelRoom) => {
    let result = setRoomColored(str, hotelRoom);

    highlight
      .split(/[\s|]/)
      .filter((word) => word)
      .forEach((word) => {
        result = result.replace(new RegExp(word, 'gi'), (match) => {
          if (checkMatch(match)) return match;
          return `<span class="highlight">${match}</span>`;
        });
      });
    return result;
  };

  const renderImageSupplement = () => {
    if (!hasImageSupplement || window.innerWidth < 768) return null;
    const url = room.hotelRooms[0].suppliments?.filter((sup) => sup.type === 'I')?.[0]?.desc;

    return (
      <div style={{ width: 80 }}>
        {!!url ? (
          <img
            src={url}
            alt=""
            style={{ width: 70, height: 40, borderRadius: 5, cursor: 'pointer' }}
            onClick={() => goToDetails({ hotel, room, filterRoom })}
          />
        ) : (
          <div className="flex-center" style={{ width: 80, height: 40 }}>
            <FontAwesomeIcon
              icon={faBed}
              style={{ width: 50, height: 40, cursor: 'pointer', color: 'var(--color-border)' }}
              onClick={() => goToDetails({ hotel, room, filterRoom })}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={'hotel-card-room ' + extraClassName + highlighted}>
      {renderImageSupplement()}
      <div className="flex-one">
        <div className="flex-row text-bold text-dark flex-between">
          <div className="flex-one" style={{ minHeight: 27 }}>
            {room.hotelRooms.map((hotelRoom, hotelRoomIndex) => (
              <div
                key={hotelRoomIndex}
                className={'text-lh-sm' + (grouped && hotelRoomIndex < room.hotelRooms.length - 1 ? ' mb-5' : '')}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: highlightStr(getRoomName(hotelRoom), filter?.room || '', hotelRoom),
                  }}
                />
              </div>
            ))}
          </div>

          {grouped && groupToggle}

          <div className="text-normal flex-row">
            <RoomIcons
              boardCode={room.board.basis.code}
              limitDate={room.cxl.limitDate}
              ada={!!room.hotelRooms[0].ada}
              hasRemarks={!!(room.remarks || room.remCondition)}
              onCancellationClick={() =>
                user?.branch?.hotelDisplaySupplierName &&
                setCancellationModalVisibleIndex(room.rcUniqueKey)
              }
              onRemarksClick={() => setRemarkModalVisibleIndex(room.rcUniqueKey)}
            />
          </div>

          <div
            className="room-last price-hover-target cur-poi"
            onClick={() => goToDetails({ hotel, room, filterRoom })}
          >
            {dynamic ? (
              <span className={getCxlClass()}>
                {formatPriceDynamic(
                  room.price[dynamic.currency as keyof Price] / dynamic.numberOfPax -
                  dynamic.choosenRoomPrice,
                  getCurrency(dynamic.currency)
                )?.replace(' ', '')}
              </span>
            ) : (
              <Tooltip
                content={formatPriceHotel(room.price, currency, priceMode).replace(/<[^>]*>/g, '')}
                position="top"
              >
                <span className="price-hover-swap">
                  <span className={`price-hover-values ${getCxlClass()}`}>
                    {user?.branch?.hotelDisplaySupplierName && (
                      <span
                        className="text-light mr-5 text-normal"
                        style={{ position: 'absolute', right: -5, top: 28 }}
                      >
                        {formatPriceHotel(room.price, room.price.currency)}
                      </span>
                    )}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPriceHotel(room.price, currency, priceMode),
                      }}
                    />
                  </span>
                  <span className="price-hover-arrow" aria-hidden>
                    <FontAwesomeIcon icon={faCircleArrowRight} />
                  </span>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex-row">
          <div className="text-light flex-one flex-col-end flex-row">
            {!dynamic && (
              <ProposalHeart
                active={!!isRoomInProposal(hotel, room)}
                onClickPropsal={(e) => {
                  e.stopPropagation();
                  toggleRoomProposal(hotel, room);
                }}
                isEnvelope={true}
              />
            )}
            {getSuppText()} {getRoomsTypes()}
          </div>
        </div>
      </div>

      {cancellationModalVisibleIndex === (cancellationModalKey ?? room.rcUniqueKey) && (
        <CancellationPolicyModal
          hotel={hotel}
          onClose={() => setCancellationModalVisibleIndex('')}
          rc={room}
        />
      )}

      {showRemarkModal && remarkModalVisibleIndex === room.rcUniqueKey && (
        <CancellationPolicyModal
          hotel={hotel}
          onClose={() => setRemarkModalVisibleIndex('')}
          rc={room}
          isRemark={true}
        />
      )}
    </div>
  );
};

export default HotelCardRoomItem;
